/**
 * Notification Query Keys
 *
 * Centralized query key factory for all notification-related queries.
 */

/**
 * Notification query key factory
 *
 * @example
 * ```typescript
 * // Get all notifications
 * useQuery({ queryKey: notificationKeys.all, queryFn: ... })
 *
 * // Get unread count
 * useQuery({ queryKey: notificationKeys.unreadCount, queryFn: ... })
 * ```
 */
export const notificationKeys = {
  /**
   * Root key for all notification queries
   */
  all: ['notifications'] as const,

  /**
   * Notification list
   */
  list: ['notifications', 'list'] as const,

  /**
   * Unread notifications
   */
  unread: ['notifications', 'unread'] as const,

  /**
   * Unread count
   */
  unreadCount: ['notifications', 'unread', 'count'] as const,

  /**
   * Matters with unread notifications
   */
  mattersWithUnread: ['notifications', 'matters-with-unread'] as const,

  /**
   * Notifications for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) =>
    [...notificationKeys.all, 'matter', matterId] as const,

  /**
   * Specific notification
   * @param notificationId - The notification ID
   */
  detail: (notificationId: number) =>
    [...notificationKeys.all, 'detail', notificationId] as const
} as const;
